/**
 * The client's copy of a closed door.
 *
 * The server owns the walk-away (`glitime_away`, see `glitime-away.ts`);
 * this caches it per account in Dexie meta, mirroring `followups.ts` and
 * `promises.ts`. The cache is for *display* only — so the door can be drawn
 * instantly on open and still be drawn with the phone offline. Enforcement is
 * the server's `chat_away` gate, which is why an evicted cache (iOS standalone
 * WebKit does that) is harmless: the first message bounces and the door
 * appears.
 *
 * The expiry check is a plain clock comparison rather than a server round
 * trip, so a door whose hour has come opens by itself, offline included.
 */

import { api } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { currentProfile } from '../screens/useProfile.js'

export type AwayKind = 'rude' | 'busy' | 'tired'
export type PriceKind = 'apology' | 'log_meal' | 'answer'

export interface AwayState {
  kind: AwayKind
  /** ISO instant she comes back. */
  until: string
  priceKind: PriceKind | null
  priceText: string | null
  priceMet: boolean
}

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.away.${owner}`
}

const cache = new Map<string, AwayState | null>()
const hydrationPromises = new Map<string, Promise<void>>()
const listeners = new Set<() => void>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = getMeta<AwayState | null>(metaKey(owner), null).then((state) => {
      if (!cache.has(owner)) cache.set(owner, state)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

function announce(): void {
  for (const listener of listeners) listener()
}

/** Subscribe to door changes — the zone redraws itself when she leaves or returns. */
export function onAwayChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function expired(state: AwayState | null): boolean {
  if (state == null) return true
  const until = Date.parse(state.until)
  return Number.isNaN(until) || until <= Date.now()
}

/**
 * The current lock, or null when she is available. Synchronous, because it is
 * read from render and from event handlers that cannot await.
 */
export function awayNow(): AwayState | null {
  const owner = ownerKey()
  void hydrate(owner)
  const state = cache.get(owner) ?? null
  if (expired(state)) {
    if (state != null) {
      // Her hour came while the app was open: forget it here rather than
      // waiting for a refresh that may never come offline.
      cache.set(owner, null)
      void setMeta(metaKey(owner), null)
      announce()
    }
    return null
  }
  return state
}

/** Stamp what a server response just told us about the door. */
export function recordAway(state: AwayState | null): void {
  const owner = ownerKey()
  if (owner === ANON_OWNER) return
  cache.set(owner, state)
  void setMeta(metaKey(owner), state)
  announce()
}

/** Pull the server's current door state. Fire-and-forget friendly. */
export async function refreshAway(): Promise<void> {
  const owner = ownerKey()
  if (owner === ANON_OWNER) return
  try {
    const wire = await api.get<{ away: AwayState | null }>('/api/ai/away')
    cache.set(owner, wire.away)
    await setMeta(metaKey(owner), wire.away)
    announce()
  } catch {
    // Offline or signed out: the cached copy keeps working, which is the point.
  }
}

export interface PriceResult {
  opened: boolean
  retort: string | null
}

/**
 * Offer her what she asked for.
 *
 * Whether it works was decided when she left, so this is one request and one
 * answer — no retrying into a different outcome.
 */
export async function payPrice(kind: PriceKind, text?: string): Promise<PriceResult> {
  try {
    const wire = await api.post<{
      opened: boolean
      retort: string | null
      away: AwayState | null
    }>('/api/ai/away/price', { kind, ...(text == null ? {} : { text }) })
    recordAway(wire.opened ? null : wire.away)
    return { opened: wire.opened, retort: wire.retort }
  } catch {
    // Offline: the door is not negotiable from here. Say so in her register
    // rather than pretending the gesture landed.
    return { opened: false, retort: 'Tavęs negirdžiu. Bandyk, kai grįš ryšys.' }
  }
}

/** Test-only: wait for an account's Dexie-backed door cache to load. */
export function __hydrateAwayForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
/** Test-only: drop the cache so the next read starts cold. */
export function __resetAwayForTests(): void {
  cache.clear()
  hydrationPromises.clear()
  listeners.clear()
}
